import { TextMentions } from "./TextMentions";
import { ChangesPanel } from "./ChangesPanel";
import { LocalAgents } from "./LocalAgents";
import { useAgentEntrances } from "./agent-entrances";

import { DraftingControls } from "./DraftingControls";
import { draftingKinds } from "../shared/drafting";
import { useCanvasAppearance } from "./useCanvasAppearance";
import { useHistoryControls } from "./history-controls";
import { isSketchSelection } from "../shared/sketch-generation";
import { SKETCH_GENERATION_ENABLED } from "../shared/features";
import { ZoomControl } from "./ZoomControl";
import { SketchTools } from "./SketchTools";
import { AccountMenu } from "./AccountMenu";
import { containingTrace } from "./tracing";
import { scaleSource, sourceMeters, sourceName } from "./source-scale";
import { TraceToolbar } from "./TraceToolbar";
import { TraceExportDialog } from "./TraceExportDialog";
import { TracePickOverlay } from "./TracePickOverlay";
import { CanvasViewControls } from "./CanvasViewControls";
import { FileDropTarget } from "./FileDropTarget";
import { SelectionToolbar } from "./SelectionToolbar";
import { api, notify } from "./api";
import { CompareDialog } from "./CompareDialog";
import { useValue } from "@tldraw/state-react";
import { toRichText, type TLShape, type TLShapeId } from "@tldraw/tlschema";
import { useEffect, useRef, useState } from "react";
import { SceneToolbar } from "./SceneToolbar";
import { SceneViewer } from "./SceneViewer";
import { commentOnPlan, type SceneRequest } from "./SceneShape";
import { readSceneSpec, type SceneSpec } from "../shared/scene-spec";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  FileText,
  Layers,
  Library,
  Link,
  Maximize,
  MessageSquare,
  Minus,
  Plus,
  Redo2,
  Undo2,
  X,
} from "lucide-react";
import { Brand } from "./Brand";
import { CommentPins } from "./CommentPins";
import { Sidebar } from "./Sidebar";
import { Inspector } from "./Inspector";
import { WorkspaceDialogs } from "./WorkspaceDialogs";
import { FileImportTray } from "./FileImportTray";
import { PdfToolbar } from "./PdfToolbar";
import { ContentToolbar } from "./ContentToolbar";
import { CreativeTools, type CreativeRequest } from "./CreativeTools";
import { ModelViewer } from "./ModelViewer";
import type { ModelAssetInfo } from "./model-assets";
import { ToolPalette, type CreationMenu } from "./ToolPalette";
import { ToolGuide } from "./ToolGuide";
import { LineAlignmentGuide } from "./LineAlignmentGuide";
import { SelectionStyle } from "./SelectionStyle";
import { DrawingToolbar } from "./DrawingToolbar";
import { useWorkspace } from "./useWorkspace";
import { DocumentFocus } from "./DocumentFocus";
import {
  createWorkspaceDocument,
  workspaceDocuments,
} from "./workspace-documents";
import { nativeDocument } from "../shared/document-session";
import type { BoardDocument } from "../shared/schema";

export function WorkspaceUI(
  props: Parameters<typeof useWorkspace>[0] & {
    signOut?: () => Promise<void>;
    libraryItem?: string;
    saveName?: (name: string) => Promise<void>;
    documentSessionId: string | null;
  },
) {
  const model = useWorkspace(props);
  const {
    editor,
    room,
    user,
    connected,
    back,
    tool,
    zoom,
    collaborators,
    sheets,
    active,
    threads,
    panel,
    setPanel,
    setTab,
    setModal,
    setThreadId,
    setShareUrl,
    canEdit,
    fileInput,
    imageInput,
    focusSheet,
    selected,
  } = model;
  useAgentEntrances(editor);
  // Comments placed from the 3D model land on the plan, at the point picked.
  const commentAt = model.canComment
    ? (shape: TLShape, p: { x: number; y: number }) => {
        model.setPanel(null);
        model.setPending({ kind: "point", shape, p });
        model.setMessage(toRichText(""));
        model.setReview({ mentions: [] });
        model.setThreadId(null);
      }
    : undefined;
  // A selected 3D model has its own toolbar.
  const sceneSelected = useValue(
    "scene selected",
    () => editor.getOnlySelectedShape()?.type === "scene",
    [editor],
  );
  const commentRef = useRef(commentAt);
  commentRef.current = commentAt;
  const [sceneOpen, setSceneOpen] = useState<{ spec: SceneSpec; shapeId: string } | null>(null);
  useEffect(() => {
    const onScene = (event: Event) => {
      const request = (event as CustomEvent<SceneRequest>).detail;
      const shape = editor.getShape(request.shapeId as TLShapeId);
      const spec = shape?.type === "scene" ? readSceneSpec(shape.props.spec) : null;
      if (!spec) return notify("This 3D model cannot be shown.");
      if (request.action === "open") setSceneOpen({ spec, shapeId: request.shapeId });
      else if (commentRef.current) {
        const error = commentOnPlan(editor, spec, request.pick, commentRef.current);
        if (error) notify(error);
      }
    };
    window.addEventListener("calque:scene", onScene);
    return () => window.removeEventListener("calque:scene", onScene);
  }, [editor]);
  const appearance = useCanvasAppearance(editor, room);
  useHistoryControls(editor);
  const historyState = useValue("undo commands", () => editor.getHistoryState(), [editor]);
  useEffect(() => {
    if (historyState.message) notify(historyState.message);
  }, [historyState.message]);
  const creativeUnlocked =
    room.role === "owner" &&
    selected.every(
      (shape) =>
        !editor.isShapeOrAncestorLocked(shape) && !editor.isShapeHidden(shape),
    );
  const creativeSketch = isSketchSelection(selected);
  const creativeAvailable =
    creativeUnlocked && SKETCH_GENERATION_ENABLED && creativeSketch;
  const draftingTool = draftingKinds.some((kind) => kind === tool);
  const drawingTool = [
    "draw",
    "line",
    "arrow",
    "geo",
    "cloud",
    "highlight",
    "polygon",
    "dimension",
    "area",
    "angle",
  ].includes(tool);
  const selectedTrace =
    model.annotation.trace ||
    (selected.length === 1 ? containingTrace(editor, selected[0]) : undefined);
  const exportTrace = selectedTrace?.parentId.startsWith("page:")
    ? selectedTrace
    : undefined;
  const [sketchIds, setSketchIds] = useState<TLShapeId[] | null>(null);
  const [traceExport, setTraceExport] = useState<TLShapeId | null>(null);
  const traceToolbar =
    selectedTrace &&
    canEdit &&
    !selectedTrace.isLocked &&
    !editor.isShapeHidden(selectedTrace) &&
    !model.pickingTrace &&
    (model.annotation.trace || selected[0]?.id === selectedTrace.id) &&
    !model.modal &&
    !traceExport
      ? selectedTrace
      : undefined;
  const contextSheet = selectedTrace || active;
  const selectedSource = scaleSource(editor, selected[0]);
  const scaleContext = selectedSource || contextSheet;
  const [selectionProperties, setSelectionProperties] = useState<string | null>(
    null,
  );
  const selectionKey = selected.map((s) => s.id).join(",");
  useEffect(() => setSelectionProperties(null), [selectionKey]);
  const [projectName, setProjectName] = useState(room.name);
  const [creativeRequest, setCreativeRequest] =
    useState<CreativeRequest | null>(null);
  const [modelRequest, setModelRequest] = useState<ModelAssetInfo | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [comparison, setComparison] = useState<TLShape[] | null>(null);
  const [documentId, setDocumentId] = useState<TLShapeId | null>(null);
  const documents = workspaceDocuments(editor);
  const writingOnCanvas = useValue(
    "writing document on canvas",
    () => nativeDocument(editor.getEditingShape()),
    [editor],
  );
  const currentDocument = documentId
    ? (editor.getShape<BoardDocument>(documentId) ?? null)
    : null;
  const openDocument = (id: TLShapeId) => {
    editor.setCurrentTool("select.idle");
    setDocumentId(id);
    setMenu(null);
    setPanel(null);
  };
  const createDocument = () => {
    if (!canEdit || !connected) {
      notify("Connect to create a document.");
      return;
    }
    const id = createWorkspaceDocument(editor);
    setMenu(null);
    setPanel(null);
    editor.setEditingShape(id);
    editor.setCurrentTool("select.editing_shape");
  };
  useEffect(() => {
    const open = (event: Event) => {
      const id = (event as CustomEvent<unknown>).detail;
      if (
        typeof id === "string" &&
        nativeDocument(editor.getShape(id as TLShapeId))
      )
        openDocument(id as TLShapeId);
    };
    window.addEventListener("calque:open-document", open);
    return () => window.removeEventListener("calque:open-document", open);
  }, [editor]);
  useEffect(() => {
    const id = new URL(window.location.href).searchParams.get("document");
    if (id && documents.some((shape) => shape.id === id)) {
      openDocument(id as TLShapeId);
      const url = new URL(window.location.href);
      url.searchParams.delete("document");
      window.history.replaceState(null, "", url);
    }
  }, [documents.map((shape) => shape.id).join("|")]);
  useEffect(() => {
    const url = new URL(window.location.href);
    const sheet = sheets.find(
      (shape) => shape.id === url.searchParams.get("sheet"),
    );
    if (!sheet) return;
    focusSheet(sheet);
    url.searchParams.delete("sheet");
    window.history.replaceState(null, "", url);
  }, [sheets.map((shape) => shape.id).join("|")]);
  const [menu, setMenu] = useState<CreationMenu>(
    props.libraryItem ? "library" : null,
  );
  const [librarySave, setLibrarySave] = useState(0);
  const [initialLibraryItem, setInitialLibraryItem] = useState(
    props.libraryItem,
  );
  useEffect(() => {
    const save = () => {
      setPanel(null);
      setMenu("library");
      setLibrarySave((n) => n + 1);
    };
    window.addEventListener("planroom:save-library", save);
    return () => window.removeEventListener("planroom:save-library", save);
  }, []);
  const [pdfProperties, setPdfProperties] = useState<string | null>(null);
  const [contentProperties, setContentProperties] = useState<string | null>(
    null,
  );
  const selectedImage =
    selected.length === 1 && selected[0].type === "image"
      ? selected[0]
      : undefined;
  const selectedAsset = selectedImage?.props.assetId
    ? editor.getAsset(selectedImage.props.assetId)
    : undefined;
  const isPdf =
    !!selectedAsset?.meta.pdfPages && !selectedImage?.meta.traceSnapshot;
  const pdfToolbarActive = isPdf && canEdit && !selectedImage?.isLocked;
  useEffect(() => setPdfProperties(null), [selectedImage?.id]);
  const selectedContent =
    selected.length === 1 &&
    ((selected[0].type === "document" && !nativeDocument(selected[0])) ||
      selected[0].type === "table" ||
      (selected[0].type === "image" && !isPdf))
      ? selected[0]
      : undefined;
  const contentToolbarActive =
    !!selectedContent && canEdit && !selectedContent.isLocked;
  useEffect(() => setContentProperties(null), [selectedContent?.id]);
  const contentObject =
    selected.length === 1 &&
    (selected[0].type === "document" ||
      selected[0].type === "table" ||
      selected[0].type === "sheet")
      ? selected[0]
      : undefined;
  // A DXF sheet's props.source is its raster preview; download the CAD original.
  const cadOriginal =
    contentObject?.type === "sheet" && contentObject.meta.cadFormat === "DXF"
      ? contentObject.meta
      : undefined;
  const documentSource =
    cadOriginal?.cadSource ||
    contentObject?.props.source ||
    selectedAsset?.meta.documentSource ||
    selectedAsset?.props.src;
  const downloadName =
    (typeof cadOriginal?.cadName === "string"
      ? cadOriginal.cadName
      : undefined) ||
    contentObject?.props.name ||
    selectedAsset?.meta.documentName ||
    (selectedAsset?.type === "image" ? selectedAsset.props.name : "Document");
  const togglePanel = (next: "drawings" | "comments" | "details") => {
    setMenu(null);
    setPanel(panel === next ? null : next);
    if (next !== "details") setTab(next);
  };
  return (
    <>
      <input
        ref={fileInput}
        className="sr-only"
        aria-label="Choose files to add"
        type="file"
        multiple
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files || []);
          event.currentTarget.value = "";
          if (files.length) model.imports.add(files);
        }}
      />
      <input
        ref={imageInput}
        className="sr-only"
        aria-label="Choose inspiration images"
        type="file"
        multiple
        accept="image/*,.svg,.avif,.heic,.heif,.tif,.tiff,.bmp,.gif"
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files || []);
          event.currentTarget.value = "";
          if (files.length) model.imports.add(files);
        }}
      />
      <FileDropTarget model={model} />
      {!sketchIds && !sceneSelected && (
        <SelectionToolbar
          model={model}
          onSketch={
            creativeAvailable && creativeSketch
              ? () => {
                  setSelectionProperties(null);
                  setSketchIds(editor.getSelectedShapeIds());
                }
              : undefined
          }
          onProperties={(id) =>
            setSelectionProperties((current) => (current === id ? null : id))
          }
        />
      )}
      {sketchIds && (
        <SketchTools
          key={sketchIds.join(",")}
          model={model}
          ids={sketchIds}
          close={() => setSketchIds(null)}
        />
      )}
      <PdfToolbar
        model={model}
        onProperties={(id) =>
          setPdfProperties((current) => (current === id ? null : id))
        }
      />
      {!creativeRequest && !modelRequest && !sceneOpen && (
        <SceneToolbar editor={editor} canEdit={canEdit} />
      )}
      {!creativeRequest && !modelRequest && (
        <ContentToolbar
          model={model}
          onCreative={(shape, mode) => {
            setContentProperties(null);
            setCreativeRequest({ shapeId: shape.id, mode });
          }}
          propertiesOpen={contentProperties === selectedContent?.id}
          onModel={(request) => {
            setContentProperties(null);
            setModelRequest(request);
          }}
          onProperties={(id) =>
            setContentProperties((current) => (current === id ? null : id))
          }
        />
      )}
      <CreativeTools
        key={`${room.id}:${user.id}`}
        model={model}
        request={creativeRequest}
        close={() => setCreativeRequest(null)}
        changeMode={(mode) =>
          setCreativeRequest((current) =>
            current ? { ...current, mode } : null,
          )
        }
      />
      {modelRequest && (
        <ModelViewer model={modelRequest} close={() => setModelRequest(null)} />
      )}
      {traceToolbar && <TraceToolbar model={model} trace={traceToolbar} />}
      {traceExport && (
        <TraceExportDialog
          editor={editor}
          id={traceExport}
          close={() => setTraceExport(null)}
        />
      )}
      <header className="workspace-header">
        <div className="project-control floating-surface">
          <button
            className="back-projects"
            onClick={back}
            aria-label="Back to projects"
            title="Back to projects"
          >
            <ArrowLeft size={18} />
          </button>
          <Brand />
          <span className="divider" />
          <div className="project-name">
            {renaming ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await api(`/api/rooms/${room.id}`, { name: projectName });
                    setRenaming(false);
                  } catch (e) {
                    notify((e as Error).message);
                  }
                }}
              >
                <input
                  aria-label="Project name"
                  autoFocus
                  maxLength={100}
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                />
                <button>Save</button>
              </form>
            ) : (
              <button
                className="project-title-button"
                aria-label="Rename project"
                disabled={!canEdit || room.platform !== "cloudflare"}
                onClick={() => {
                  if (canEdit && room.platform === "cloudflare")
                    setRenaming(true);
                }}
                title={canEdit ? "Rename project" : undefined}
              >
                <strong>{projectName}</strong>
                {canEdit && room.platform === "cloudflare" && (
                  <ChevronDown size={13} />
                )}
              </button>
            )}
            <span>
              {room.demo
                ? "Example project · synthetic plan"
                : "Design workspace"}
            </span>
          </div>
        </div>
        <div className="collaboration-control floating-surface">
          <button
            aria-label="My Library"
            aria-pressed={menu === "library"}
            onClick={() => {
              setPanel(null);
              setMenu(menu === "library" ? null : "library");
            }}
          >
            <Library size={17} />
            <span>My Library</span>
          </button>
          <span
            className={`connection ${connected ? "" : "offline"}`}
            role="status"
          >
            {connected ? "Live" : "Reconnecting…"}
          </span>
          <div className="avatar-stack">
            {!props.signOut && (
              <span className="avatar" title={`${user.name} (you)`}>
                {user.name[0].toUpperCase()}
              </span>
            )}
            {collaborators
              .filter(
                (p, i, a) => a.findIndex((q) => q.userId === p.userId) === i,
              )
              .map((p) => (
                <span
                  className="avatar collaborator"
                  key={p.userId}
                  title={p.userName}
                  style={{ background: p.color }}
                >
                  {p.userName?.slice(0, 1).toUpperCase()}
                </span>
              ))}
          </div>
          <span className="header-separator" aria-hidden="true" />
          <ChangesPanel key={`${room.id}:${user.id}`} model={model} />
          <button
            className="header-share"
            onClick={() => {
              setShareUrl("");
              setModal("share");
            }}
          >
            <Link size={16} />
            Share
          </button>
          <button
            className="header-export"
            aria-label={exportTrace ? "Export tracing sheet" : "Export PDF"}
            disabled={!active && !exportTrace}
            title={
              !active && !exportTrace
                ? "Add a sheet to export a PDF"
                : exportTrace
                  ? "Export tracing sheet"
                  : "Export PDF"
            }
            onClick={() => {
              if (!exportTrace) {
                setModal("export");
                return;
              }
              setTraceExport(exportTrace.id);
            }}
          >
            <Download size={16} />
            <span>{exportTrace ? "Export tracing sheet…" : "Export PDF"}</span>
          </button>
          {
            <>
              <span className="header-separator" aria-hidden="true" />
              <AccountMenu
                user={user}
                saveName={props.saveName}
                signOut={props.signOut}
              />
            </>
          }
        </div>
      </header>
      <nav
        className="board-navigation floating-surface"
        aria-label="Board content"
      >
        <button
          aria-expanded={panel === "drawings"}
          className={panel === "drawings" ? "active" : ""}
          onClick={() => togglePanel("drawings")}
        >
          <Layers size={17} />
          Contents
          <span className="count">
            {sheets.length +
              documents.length +
              model.shapes.filter((s) => s.type === "trace").length}
          </span>
        </button>
        <span className="divider" />
        <button
          aria-expanded={panel === "comments"}
          className={panel === "comments" ? "active" : ""}
          onClick={() => togglePanel("comments")}
        >
          <MessageSquare size={17} />
          Comments
          {threads.some((t) => !t.resolved) && (
            <span className="count">
              {threads.filter((t) => !t.resolved).length}
            </span>
          )}
        </button>
      </nav>
      <ToolPalette
        model={model}
        menu={menu}
        setMenu={setMenu}
        onCreateDocument={createDocument}
        saveRequest={librarySave}
        initialLibraryItem={initialLibraryItem}
        onLibraryItemHandled={() => setInitialLibraryItem(undefined)}
        onLibrarySaveHandled={() => setLibrarySave(0)}
      />
      {panel && (
        <div
          className="workspace-panel floating-surface"
          aria-label={
            panel === "details"
              ? "Sheet details"
              : panel === "drawings"
                ? "Contents panel"
                : "Comments panel"
          }
        >
          <div className="workspace-panel-heading">
            <strong>
              {panel === "details"
                ? "Sheet details"
                : panel === "drawings"
                  ? "Contents"
                  : "Comments"}
            </strong>
            <button aria-label="Close panel" onClick={() => setPanel(null)}>
              <X size={18} />
            </button>
          </div>
          {panel === "details" ? (
            <Inspector model={model} />
          ) : (
            <Sidebar
              model={model}
              onOpenDocument={openDocument}
              onCreateDocument={createDocument}
              onNewSheet={() => {
                setPanel(null);
                setMenu("sheet");
              }}
            />
          )}
        </div>
      )}
      {contextSheet && (
        <div className="drawing-context floating-surface">
          <button
            className="drawing-context-name"
            onClick={() =>
              selectedTrace
                ? editor.select(selectedTrace.id)
                : togglePanel("details")
            }
            aria-expanded={panel === "details"}
            aria-label={
              selectedTrace ? "Select tracing sheet" : "Sheet details"
            }
          >
            <FileText size={17} />
            <span>{contextSheet.props.name}</span>
            <ChevronDown size={15} />
          </button>
          <span className="divider" />
          <span className="sheet-scale-status">
            {scaleContext
              ? `${scaleContext.type === "sheet" ? "Sheet" : sourceName(scaleContext)} scale · ${
                  sourceMeters(editor, scaleContext) ? "set" : "not set"
                }`
              : "Scale · not set"}
          </span>
        </div>
      )}
      {canEdit && draftingTool && !panel && !model.modal && (
        <div className="drafting-tool-settings floating-surface">
          <DraftingControls model={model} />
        </div>
      )}
      {canEdit && drawingTool && !panel && !model.modal && (
        <DrawingToolbar key={tool} model={model} />
      )}
      {canEdit &&
        !writingOnCanvas &&
        !drawingTool &&
        !draftingTool &&
        tool !== "move" &&
        tool !== "text" &&
        (!pdfToolbarActive || pdfProperties === selectedImage?.id) &&
        (!contentToolbarActive || contentProperties === selectedContent?.id) &&
        !(traceToolbar && !model.annotation.trace) &&
        !panel &&
        !model.cropping &&
        !model.pickingTrace &&
        (model.annotation.trace ||
          selectionProperties === selectionKey ||
          pdfProperties === selectedImage?.id ||
          contentProperties === selectedContent?.id ||
          selected.length === 0) &&
        !selected.some((s) => editor.isShapeOrAncestorLocked(s)) &&
        (selected.length > 0 ||
          ![
            "select",
            "hand",
            "pin",
            "comment-area",
            "calibrate",
            "trace-paper",
          ].includes(tool)) && (
          <SelectionStyle
            key={selected.map((s) => s.id).join(",") || tool}
            model={model}
            onClose={() => {
              setSelectionProperties(null);
              setPdfProperties(null);
              setContentProperties(null);
            }}
            onSketch={() => {
              setSelectionProperties(null);
              setSketchIds(editor.getSelectedShapeIds());
            }}
          />
        )}
      {selected.length === 2 &&
        selected.every((s) => ["sheet", "image"].includes(s.type)) && (
          <button
            className="compare-trigger floating-surface"
            onClick={() => setComparison([...selected])}
          >
            Compare selected
          </button>
        )}
      {!pdfToolbarActive &&
        !contentToolbarActive &&
        !(canEdit && contentObject?.type === "sheet") &&
        typeof documentSource === "string" &&
        /^\/api\/rooms\/[\w-]+\/files\/[\w-]+$/.test(documentSource) && (
          <a
            className="document-download floating-surface"
            href={documentSource}
            download={String(downloadName)}
          >
            <Download size={17} /> Download original
            <span>{String(downloadName)}</span>
          </a>
        )}
      {selected.length > 1 && selected.every((s) => s.type === "sheet") && (
        <button
          className="fit-selected floating-surface"
          onClick={() => editor.zoomToSelection()}
        >
          Fit selected sheets
        </button>
      )}
      {comparison && (
        <CompareDialog
          editor={editor}
          shapes={comparison}
          close={() => setComparison(null)}
        />
      )}
      {model.pickingTrace && (
        <TracePickOverlay model={model} traceId={model.pickingTrace} />
      )}
      <ToolGuide model={model} />
      <LineAlignmentGuide editor={editor} />
      <div
        className="navigation-controls floating-surface"
        aria-label="Canvas navigation"
      >
        <button
          aria-label="Zoom out"
          title="Zoom out"
          onClick={() => editor.zoomOut()}
        >
          <Minus size={16} />
        </button>
        <ZoomControl editor={editor} zoom={zoom} />
        <button
          aria-label="Zoom in"
          title="Zoom in"
          onClick={() => editor.zoomIn()}
        >
          <Plus size={16} />
        </button>
        <span className="divider" />
        {canEdit && (
          <>
            <button
              aria-label="Undo"
              title={historyState.undo ? `Undo ${historyState.undo}` : "Nothing to undo"}
              disabled={!historyState.undo}
              onClick={() => editor.undo()}
            >
              <Undo2 size={16} />
            </button>
            <button
              aria-label="Redo"
              title={historyState.redo ? `Redo ${historyState.redo}` : "Nothing to redo"}
              disabled={!historyState.redo}
              onClick={() => editor.redo()}
            >
              <Redo2 size={16} />
            </button>
          </>
        )}
        <CanvasViewControls model={model} appearance={appearance} />
      </div>
      <CommentPins model={model} />
      <TextMentions model={model} />
      {canEdit && user.features?.agents && (
        <LocalAgents
          key={room.id}
          editor={editor}
          room={room}
          connected={connected}
          userId={user.id}
          importFiles={model.imports.add}
          commentAt={commentAt}
        />
      )}
      {sceneOpen && (
        <SceneViewer
          scene={sceneOpen.spec}
          close={() => setSceneOpen(null)}
          commentOnPlan={
            commentAt
              ? (spec, pick) => {
                  setSceneOpen(null);
                  const error = commentOnPlan(editor, spec, pick, commentAt);
                  if (error) notify(error);
                }
              : undefined
          }
        />
      )}
      {canEdit && <FileImportTray model={model} />}
      <WorkspaceDialogs model={model} />
      {documentId && (
        <DocumentFocus
          model={model}
          shape={currentDocument}
          sessionId={props.documentSessionId}
          connected={connected}
          initiallyEdit={false}
          onClose={() => {
            setDocumentId(null);
            editor.focus();
          }}
          onOpen={(id) => openDocument(id as TLShapeId)}
        />
      )}
    </>
  );
}
